import React from 'react';
import PropTypes from 'prop-types';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import shortId from 'shortid';

// - Components
import Button from '@material-ui/core/Button';
import AddSectionDialog from './components/AddSectionDialog';
import AttributeCard from './components/AttributeCard';
import CategoryCard from './components/CategoryCard';
import SidebarCard from './components/SidebarCard';
import MockField from './components/MockField';
import Field from './components/Field';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import Loader from 'components/Loader';
import { Prompt } from 'react-router';

// - Page
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageTitle from 'components/Page/PageTitle';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';

// - Loader
import withStyles from '@material-ui/core/styles/withStyles';
import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

const SortableList = SortableContainer(
  ({
    layout,
    layoutType,
    handleChange,
    onLayoutChange,
    getValidAttributes,
    removeSection,
    onSectionSortEnd,
    typeKey,
  }) => {
    return (
      <div>
        {layout.map((item, index) => {
          return (
            <SortableItemElem
              key={`card-${index}-${item.value.displayName}-${item.type}`}
              index={index}
              value={item}
              onLayoutChange={onLayoutChange}
              itemIndex={index}
              handleChange={handleChange}
              getValidAttributes={getValidAttributes}
              removeSection={removeSection}
              onSectionSortEnd={onSectionSortEnd}
              layout={layout}
              layoutType={layoutType}
              typeKey={typeKey}
            />
          );
        })}
      </div>
    );
  },
);

const SortableItemElem = SortableElement(
  ({
    value,
    itemIndex,
    layout,
    layoutType,
    handleChange,
    getValidAttributes,
    removeSection,
    onSectionSortEnd,
    onLayoutChange,
    typeKey,
  }) => {
    if (layoutType === 'sidebarLayout') {
      return (
        <div>
          <Field attribute={value} secondLevel />
        </div>
      );
    } else if (value.type === 'category') {
      return (
        <CategoryCard
          typeKey={typeKey}
          category={value}
          handleChange={handleChange}
          getValidAttributes={getValidAttributes}
          removeSection={removeSection}
          onSortEnd={onSectionSortEnd}
          onLayoutChange={onLayoutChange}
          itemIndex={itemIndex}
          layout={layout}
        />
      );
    } else if (value.type === 'field') {
      return (
        <AttributeCard
          attribute={value}
          removeSection={removeSection}
          onLayoutChange={onLayoutChange}
          itemIndex={itemIndex}
          layout={layout}
        />
      );
    }
    return null;
  },
);

class ProfileLayout extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    entityType: PropTypes.object.isRequired,
    globalConfig: PropTypes.object.isRequired,
    entityTypesStatus: PropTypes.number,
    globalStatus: PropTypes.number,
    getValidAttributes: PropTypes.func.isRequired,
    layout: PropTypes.array.isRequired,
    layoutItemTemplate: PropTypes.object.isRequired,
    handleUpdateLayout: PropTypes.func.isRequired,
    sidebarLayout: PropTypes.array.isRequired,
    typeKey: PropTypes.string.isRequired,
    isIndependentPage: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    const currentLayout = [...props.layout];
    const sidebarLayout = [...props.sidebarLayout];
    const categories = _cloneDeep(props.entityType._categories);
    this.state = {
      currentLayout,
      sidebarLayout,
      categories,
      open: false,
      dirty: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !_isEqual(prevProps.layout, this.props.layout) ||
      !_isEqual(prevProps.sidebarLayout, this.props.sidebarLayout)
    ) {
      this.setState({ currentLayout: this.props.layout, sidebarLayout: this.props.sidebarLayout });
    }
  }

  updateLayout = () => {
    const { typeKey } = this.props;
    const data = !_isEqual(this.state.currentLayout, this.props.layout)
      ? {
          entityField: typeKey,
          layoutKey: typeKey === PERSON_ENTITY_TYPE_KEY ? 'web_profile' : 'profile-info',
          // (notes - pearl) omitting shortId before sending data to api
          children: this.state.currentLayout.map(l => _omit(l, 'shortId')),
        }
      : null;

    const sidebarData =
      typeKey === PERSON_ENTITY_TYPE_KEY &&
      !_isEqual(this.state.sidebarLayout, this.props.sidebarLayout)
        ? {
            entityField: typeKey,
            layoutKey: 'profile-people',
            children: this.state.sidebarLayout,
          }
        : null;

    const layoutsToUpdate = [...(data ? [data] : []), ...(sidebarData ? [sidebarData] : [])];
    this.props.handleUpdateLayout(layoutsToUpdate);
  };

  handleChange = updatedCategory => {
    this.state.currentLayout.forEach((layoutItem, index) => {
      if (!layoutItem.name) {
        if (layoutItem.shortId === updatedCategory.shortId) {
          this.setState(prevState => {
            const currentLayout = [...prevState.currentLayout];
            currentLayout[index] = updatedCategory;
            return { currentLayout };
          });
        }
      } else if (layoutItem.name === updatedCategory.name) {
        this.setState(prevState => {
          const currentLayout = [...prevState.currentLayout];
          currentLayout[index] = updatedCategory;
          return { currentLayout };
        });
      }
    });
  };

  handleAddAttributes = attributes => {
    let newLayout = _cloneDeep(this.state.currentLayout);
    const createdAttributes = attributes.map(attribute => {
      const attributeTemplate = _cloneDeep(this.props.layoutItemTemplate);
      attributeTemplate.name = attribute.objectKey;
      attributeTemplate.type = 'field';
      attributeTemplate.value.id = attribute.id;
      attributeTemplate.value.displayName = attribute.displayName;
      attributeTemplate.value.entityField = attribute.entityField;
      attributeTemplate.value.children = undefined;
      return attributeTemplate;
    });
    createdAttributes.forEach(createdAttribute => {
      newLayout.push(createdAttribute);
    });
    this.setState({ currentLayout: newLayout });
  };

  handleCreateCategory = categoryName => {
    const newLayout = _cloneDeep(this.state.currentLayout);
    const categoryTemplate = _cloneDeep(this.props.layoutItemTemplate);
    // (notes - pearl) name needs to be undefined or one that exists in db
    categoryTemplate.name = undefined;
    // (notes - pearl) shortId is for FE use only, to identify the category being edited
    categoryTemplate.shortId = shortId();
    categoryTemplate.type = 'category';
    categoryTemplate.value.id = undefined;
    categoryTemplate.value.displayName = categoryName;
    categoryTemplate.value.entityField = this.props.typeKey;
    categoryTemplate.value.children = [];
    newLayout.push(categoryTemplate);
    this.setState({ currentLayout: newLayout });
  };

  handleRemoveSection = sectionId => {
    let newLayout = _cloneDeep(this.state.currentLayout);
    const removeIndex = newLayout.findIndex(layoutItem => sectionId === layoutItem.name);
    newLayout.splice(removeIndex, 1);
    this.setState({ currentLayout: newLayout });
  };

  onSortEnd = ({ oldIndex, newIndex }, type) => {
    this.setState(state => {
      return { [type]: arrayMove(state[type], oldIndex, newIndex) };
    });
  };

  onSectionSortEnd = (sectionIndex, { oldIndex, newIndex }) => {
    const section = this.state.currentLayout[sectionIndex];
    section.value.children = arrayMove(section.value.children, oldIndex, newIndex);
    this.setState({ currentLayout: this.state.currentLayout });
  };

  handleClickOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  resetLayout = () => {
    this.setState({ currentLayout: this.props.layout, sidebarLayout: this.props.sidebarLayout });
  };

  render() {
    const dirty =
      !_isEqual(this.props.layout, this.state.currentLayout) ||
      !_isEqual(this.props.sidebarLayout, this.state.sidebarLayout);

    const { classes, entityTypesStatus } = this.props;

    return (
      <Loader status={entityTypesStatus} classes={{ root: classes.statusRoot }}>
        <PageTitle
          subtitle="Build the layout for your user profiles. Add, remove, and update attributes or categories (sections) of attributes."
          actions={
            <Button
              onClick={this.handleClickOpen}
              className={this.props.classes.addSectionButton}
              variant="outlined"
              color="secondary"
            >
              Add Sections
            </Button>
          }
        />
        <div className={classes.sortableListContainer}>
          <div className={classes.leftColumn}>
            <SortableList
              layout={this.state.currentLayout}
              onSortEnd={value => this.onSortEnd(value, 'currentLayout')}
              onLayoutChange={value => this.onSortEnd(value, 'currentLayout')}
              onSectionSortEnd={this.onSectionSortEnd}
              useDragHandle
              handleChange={this.handleChange}
              getValidAttributes={this.props.getValidAttributes}
              removeSection={this.handleRemoveSection}
              typeKey={this.props.typeKey}
            />
          </div>
          <div className={classes.rightColumn}>
            {!this.props.isIndependentPage && (
              <SidebarCard>
                <MockField
                  displayName={
                    this.props.typeKey === PERSON_ENTITY_TYPE_KEY ? 'Team Leader' : 'Page Members'
                  }
                  secondLevel
                />
                {this.props.typeKey === PERSON_ENTITY_TYPE_KEY && (
                  <>
                    <MockField displayName={'Team'} secondLevel />
                    <SortableList
                      layout={this.state.sidebarLayout}
                      onSortEnd={value => this.onSortEnd(value, 'sidebarLayout')}
                      onSectionSortEnd={this.onSectionSortEnd}
                      useDragHandle
                      handleChange={value => this.handleChange(value, 'sidebarLayout')}
                      getValidAttributes={this.props.getValidAttributes}
                      removeSection={this.handleRemoveSection}
                      layoutType={'sidebarLayout'}
                      typeKey={PERSON_ENTITY_TYPE_KEY}
                    />
                  </>
                )}
              </SidebarCard>
            )}
          </div>
        </div>
        <PageFormToolbar dirty={dirty}>
          <CancelButton onClick={this.resetLayout} />
          <SaveButton onClick={this.updateLayout} disabled={!dirty} />
        </PageFormToolbar>
        <AddSectionDialog
          open={this.state.open}
          handleClose={this.handleClose}
          handleAddAttributes={this.handleAddAttributes}
          handleCreateCategory={this.handleCreateCategory}
          currentLayout={this.state.currentLayout || []}
          typeKey={this.props.typeKey}
        />
        <Prompt when={dirty} message="Your work is not saved! Are you sure you want to leave?" />
      </Loader>
    );
  }
}

const styles = theme => ({
  sortableListContainer: {
    display: 'flex',
    width: '100%',
  },
  leftColumn: {
    minWidth: 500,
    marginRight: theme.spacing(2),
    flex: '1 1 auto',
  },
  rightColumn: {
    minWidth: 340,
    flex: '1 1 auto',
  },
  statusRoot: {
    position: 'static',
  },
  addSectionButton: {
    marginTop: theme.spacing(2),
  },
});

export default withStyles(styles)(ProfileLayout);
